<script setup lang="ts">
import { computed, ref } from 'vue'

const props = defineProps<{
  name?: string
  value: unknown
  depth?: number
}>()

const open = ref((props.depth ?? 0) < 2)

const entries = computed(() => {
  if (props.value === null || typeof props.value !== 'object') {
    return []
  }

  if (Array.isArray(props.value)) {
    return props.value.map((value, index) => [String(index), value] as const)
  }

  return Object.entries(props.value as Record<string, unknown>)
})

const isBranch = computed(() => entries.value.length > 0)
const typeLabel = computed(() => {
  if (Array.isArray(props.value)) {
    return `Array(${props.value.length})`
  }

  if (props.value === null) {
    return 'null'
  }

  return typeof props.value === 'object' ? 'Object' : typeof props.value
})
</script>

<template>
  <div class="json-node" :style="{ '--depth': depth ?? 0 }">
    <button
      v-if="isBranch"
      class="json-line"
      type="button"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="json-caret">{{ open ? '−' : '+' }}</span>
      <span v-if="name" class="json-key">{{ name }}</span>
      <span class="json-type">{{ typeLabel }}</span>
    </button>
    <div v-else class="json-line leaf">
      <span v-if="name" class="json-key">{{ name }}</span>
      <span class="json-value">{{ JSON.stringify(value) }}</span>
    </div>

    <div v-if="open && isBranch" class="json-children">
      <JsonTree
        v-for="[childName, childValue] in entries"
        :key="childName"
        :name="childName"
        :value="childValue"
        :depth="(depth ?? 0) + 1"
      />
    </div>
  </div>
</template>
